import React from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header';
import PaymentStatus from './components/PaymentStatus';
import Home from './pages/Home';
import Account from './pages/Account';
import ProtectedRoute from './components/ProtectedRoute';
import News from './components/News';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from './api/stripe';
import styled from 'styled-components';
import './App.css';
import EditProfile from "./components/EditProfile";
import ChangePassword from "./components/ChangePassword";
import ChangeBillingDetails from "./components/ChangeBillingDetails";
import ProfileSettings from "./components/ProfileSettings";
import CommentNotification from "./components/CommentNotifications";
import PostDetail from "./components/PostDetail";
import CookieBanner from "./components/CustomCookieConsent";
import FAQ from "./components/Faq";
import DownloadPage from "./components/DownloadPage";
import SubscriptionDetails from "./components/SubscriptionDetails";
import BettingTipsList from "./components/BettingTipsList";
import Hero from "./landing/Hero";
import FullHome from "./landing/FullHome";
import Admin from "./pages/Admin";
import Invoicing from "./pages/Invoicing";
import Campaigns from "./pages/Campaigns";
import Security from "./pages/Security";
import StepUpGate from "./components/StepUpGate";
import StepUpModalHost from "./components/StepUpModalHost";
import TermsOfService from "./pages/TermsOfService";
import TermsOfServiceArchive from "./pages/TermsOfServiceArchive";


// Create a proper app container
const AppContainer = styled.div`
    min-height: 100vh;
    background-color: #f8f9fa;
`;

// The app is mobile-first and capped at 600px everywhere. $fullWidth lifts that cap for the few
// routes that genuinely need the room — the landing page, and the campaign composer, where the
// editor and the live e-mail preview sit side by side.
const MainContainer = styled.div<{ $isLandingPage?: boolean; $fullWidth?: boolean }>`
    width: 100%;
    max-width: ${props => props.$fullWidth ? '100%' : '600px'};
    margin: 0 auto;
    background-color: ${props => props.$isLandingPage ? 'transparent' : '#f8f9fa'};
`;

const HeaderWrapper = styled.div<{ $isLandingPage?: boolean; $fullWidth?: boolean }>`
    width: 100%;
    max-width: ${props => props.$fullWidth ? '100%' : '600px'};
    margin: 0 auto;
    background-color: ${props => props.$isLandingPage ? 'transparent' : '#f8f9fa'};
`;

// Component to conditionally render header based on route
const Layout: React.FC = () => {
    const location = useLocation();
    const isLandingPage = location.pathname === '/domov';
    // Full width, but unlike the landing page it keeps the header and the normal background.
    // The security page joins the list because its session table is six columns wide.
    const isFullWidth =
        isLandingPage || location.pathname === '/kampane' || location.pathname === '/bezpecnost'
        || location.pathname === '/admin';

    React.useEffect(() => {
        if (location.pathname === '/domov') {
            const hash = location.hash.substring(1); // remove #
            let source = null;
            if (hash === 'fromig') {
                source = 'instagram';
            } else if (hash === 'fromyt') {
                source = 'youtube';
            } // Add more mappings as needed
            if (source && !localStorage.getItem('referral_source')) {
                localStorage.setItem('referral_source', source);
            }
        }
    }, [location]);

    return (
        <>
            {!isLandingPage && (
                <HeaderWrapper $isLandingPage={isLandingPage} $fullWidth={isFullWidth}>
                    <Header />
                </HeaderWrapper>
            )}
            <MainContainer $isLandingPage={isLandingPage} $fullWidth={isFullWidth}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/domov" element={<FullHome />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/aplikacia" element={<DownloadPage />} />
                    <Route path="/databaza-tipov" element={<BettingTipsList />} />
                    {/* /admin renders its own login modal; comment moderation owns the strict 2FA gate. */}
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/podmienky" element={<TermsOfService />} />
                    <Route path="/podmienky-archiv" element={<TermsOfServiceArchive />} />

                    <Route element={<ProtectedRoute />}>
                        <Route path="/account" element={<Account />} />
                        <Route path="/payment-status" element={
                            <Elements stripe={stripePromise}>
                                <SubscriptionDetails></SubscriptionDetails>
                                <PaymentStatus />
                            </Elements>
                        } />
                        <Route path="/news" element={<News />} />
                        <Route path="/edit-profile" element={<EditProfile/>} />
                        <Route path="/change-password" element={< ChangePassword/>} />
                        <Route path="/change-billing-details" element={<ChangeBillingDetails/>} />
                        <Route path="/profile-settings" element={<ProfileSettings/>} />
                        {/* Owner-only invoicing (also gated inside the component + by the API) */}
                        <Route
                            path="/faktury"
                            element={
                                <StepUpGate strict>
                                    <Invoicing />
                                </StepUpGate>
                            }
                        />
                        {/* Owner-only e-mail campaigns. Full-width page — the live preview needs the room. */}
                        <Route
                            path="/kampane"
                            element={
                                <StepUpGate strict>
                                    <Campaigns />
                                </StepUpGate>
                            }
                        />
                        {/* Owner-only security page — never wrapped in StepUpGate; it is the way in. */}
                        <Route path="/bezpecnost" element={<Security />} />
                        {/* Retired 2026-09-10. Invoices come from Stripe now (its e-mails and portal), so the
                            in-app order list and its legacy iText PDFs had nothing true to show. Old links and
                            bookmarks land on the account page instead of an empty layout. */}
                        <Route path="/order-history" element={<Navigate to="/account" replace />} />
                        <Route path="/comment-notification" element={<CommentNotification />} />
                        <Route path="/post/:id" element={<PostDetail />} />
                        {/* Retired 2026-09-10 together with the web chat (ChatBubble, ChatHeader,
                            WebSocketService): the live chat lives in the mobile app only. */}
                        <Route path="/messages/*" element={<Navigate to="/" replace />} />
                        <Route path="/predplatne/" element={<SubscriptionDetails />} />
                    </Route>
                </Routes>
            </MainContainer>
            {/* One prompt for the whole app, outside the routed tree so it survives a navigation and
                cannot be duplicated by two gated pages. Inside the Router: it navigates. */}
            <StepUpModalHost />
            <CookieBanner />
        </>
    );
};

const App: React.FC = () => {
    return (
        <AppContainer id="app-root"> {/* This ID is important */}
            {/* Inside AppContainer, outside Router: a render error anywhere in the routed tree
                shows the fallback in place of the page instead of unmounting React's root and
                leaving a blank white document. */}
            <ErrorBoundary>
                <Router>
                    <Layout />
                </Router>
            </ErrorBoundary>
        </AppContainer>
    );
};

export default App;
